import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sparkles } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { to: '/', label: 'Inicio' },
    { to: '/nosotros', label: 'Nosotros' },
    { to: '/aps', label: 'APIs SUNAT' },
    { to: '/blog', label: 'Blog' },
    { to: '/galeria', label: 'Galería' },
    { to: '/documentacion', label: 'Documentación' },
    { to: '/contacto', label: 'Contacto' }
  ];

  const isActiveLink = (path) => {
    return location.pathname === path;
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent('Hola, estoy interesado en comenzar con las APIs SUNAT. ¿Me pueden ayudar?');
    window.open(`https://wa.me/51955000321?text=${message}`, '_blank');
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-200/50' 
          : 'bg-transparent border-b border-white/20'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 max-w-7xl">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-sm">V7</span>
              </div>
              <motion.div
                className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 bg-clip-text text-transparent leading-tight">
                Visioner7
              </span>
              <span className="text-xs text-gray-500 font-medium -mt-1">APIs SUNAT</span>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <motion.div
                key={item.to}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="relative"
              >
                <Link
                  to={item.to}
                  className={`px-5 py-3 rounded-xl font-medium transition-all duration-300 group relative ${
                    isActiveLink(item.to)
                      ? 'text-teal-600 bg-teal-50 shadow-md'
                      : 'text-gray-700 hover:text-teal-600 hover:bg-teal-50/50'
                  }`}
                >
                  <span className="text-sm font-semibold">{item.label}</span>

                  {/* Active indicator */}
                  {isActiveLink(item.to) && (
                    <motion.div
                      className="absolute inset-0 rounded-xl bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-200/50 -z-10"
                      layoutId="activeNav"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}

                  {/* Hover effect */}
                  <motion.div
                    className="absolute bottom-2 left-1/2 w-1 h-1 bg-teal-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={false}
                    animate={{ scale: isActiveLink(item.to) ? 1 : 0 }}
                  />
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <motion.button
              onClick={openWhatsApp}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 10px 30px -5px rgba(45, 212, 191, 0.3)"
              }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2 group"
            >
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-semibold">Comenzar Gratis</span>
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden p-3 rounded-xl bg-white/80 backdrop-blur-sm shadow-lg hover:shadow-xl border border-gray-200/50"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.95 }}
            whileHover={{ scale: 1.05 }}
            aria-label="Toggle menu"
          >
            <AnimatePresence mode="wait">
              {isOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                >
                  <X size={20} className="text-gray-700" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                >
                  <Menu size={20} className="text-gray-700" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border border-gray-200/50 mt-3 rounded-2xl shadow-2xl"
            >
              <nav className="flex flex-col space-y-1 py-4 px-4">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={item.to}
                      className={`flex items-center justify-between px-4 py-4 rounded-xl font-medium transition-all duration-300 ${
                        isActiveLink(item.to)
                          ? 'text-teal-600 bg-teal-50 shadow-md'
                          : 'text-gray-700 hover:text-teal-600 hover:bg-teal-50'
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="text-sm font-semibold">{item.label}</span>
                      {isActiveLink(item.to) && (
                        <motion.div
                          className="w-2 h-2 bg-teal-500 rounded-full"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                ))}
                
                {/* Mobile CTA Button */}
                <div className="pt-4 border-t border-gray-200/50 mt-4">
                  <motion.button
                    onClick={() => {
                      openWhatsApp();
                      setIsOpen(false);
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-4 py-4 rounded-xl font-semibold shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-semibold">Comenzar Gratis</span>
                  </motion.button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;